import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { prop } from '@ipfabric/utils/fp/prop';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { refreshTokens } from 'components/GlobalState/commands/refreshTokens';
import { saveIpfNewVersion } from 'components/GlobalState/commands/saveIpfNewVersion';
import { useSocketConnection } from 'components/Socket/useSocketConnection';
import {
  jobAdded,
  jobCancelled,
  jobQueued,
  jobStarted,
  jobUpdated,
} from 'containers/App/Header/JobManagement/actions';
import { makeSelectDiscoveryRunByUserId } from 'containers/App/Header/JobManagement/selectors';
import { REFRESH_DIALOGUE_NAME } from 'containers/App/constants';
import { toggleConfirmDialog } from 'containers/ConfirmDialog/actions';
import { DISCOVERY_WIZARD_FINAL_STEP_PATH } from 'features/DiscoverySettingsForms/composition/DiscoveryConfigWizard/wizardConfig';
import get from 'lodash/get';
import {
  enableStartBtn,
  fetchSnapshotData,
  fetchSnapshotManagement,
  networkBandwidthSet,
  snapshotStatusReceived,
} from 'pages/SnapshotManagement/actions';
import { supportStatusQueryKey } from 'pages/Support/Status/useSupportStatusQuery';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  authenticate,
  connectSocket,
  disconnectSocket,
  ipfSocket,
  joinRoom,
  rooms,
} from './ipfSocket';

export const useSocket = (): void => {
  useSocketConnection();

  const navigate = useNavigate();
  const reduxDispatch = useDispatch();
  const globalStateCtxProps = useGlobalStateContextSelectors({
    dispatch: prop('dispatch'),
    auth: prop('auth'),
    loginStatus: prop('loginStatus'),
  });

  const runByUser = useSelector<unknown, boolean>(
    makeSelectDiscoveryRunByUserId(globalStateCtxProps.auth.id),
  );

  const snapshotStart = useStableCallback((message: string) => {
    const { pathname } = window.location;
    if (runByUser && pathname.startsWith('/snapshot-management/new')) {
      reduxDispatch(
        fetchSnapshotManagement({
          selectSnapshotKey: get(message, 'vCollect._key'),
          enableStartBtn: true,
          finishStatus: get(message, 'vCollect.finishStatus'),
          preventDeviceLoad: true,
        }),
      );
      reduxDispatch(snapshotStatusReceived(message));
    } else {
      const payload = { enableStartBtn: true };
      reduxDispatch(
        fetchSnapshotManagement(
          window.location.pathname === DISCOVERY_WIZARD_FINAL_STEP_PATH
            ? {
                ...payload,
                selectSnapshotKey: get(message, 'vCollect._key'),
              }
            : payload,
        ),
      );
    }
  });

  const loginStatus = useRef(globalStateCtxProps.loginStatus);

  useEffect(() => {
    loginStatus.current = globalStateCtxProps.loginStatus;
  }, [globalStateCtxProps.loginStatus]);

  const queryClient = useQueryClient();

  // biome-ignore lint/correctness/useExhaustiveDependencies(globalStateCtxProps.dispatch): stable
  useEffect(() => {
    ipfSocket.socket.on('connect', () => {
      ipfSocket.socket.once('disconnect', () => {
        ipfSocket.socketIsConnected = false;
      });

      // skip authentication if loginStatus is UNINITIALIZED or LOGGED_OUT because it would fail anyway.
      if (loginStatus.current !== 'AUTHENTICATED') {
        return;
      }
      authenticate();
    });

    ipfSocket.socket.on('authenticated', () => {
      ipfSocket.socketIsConnected = true;
      joinRoom(rooms.snapshot);
      joinRoom(rooms.systemStatus);
    });

    ipfSocket.socket.on('unauthorized', async (msg: string) => {
      try {
        // UNINITIALIZEDs could happend only in case of very slow connection or breakpoint in code when app
        // doesn't have enough time to athencticate user.
        // LOGGED_OUT is state when user is logged out manualy or by application and we don't want to refresh tokens.
        if (loginStatus.current !== 'AUTHENTICATED') {
          return;
        }
        await globalStateCtxProps.dispatch(refreshTokens);
        // when server emit unauthorized event it also disconnect websocket so we need to reconnect it after obtain
        // new tokens
        connectSocket();
      } catch {
        disconnectSocket();
        connectSocket();

        if (process.env.NODE_ENV === 'development') {
          // Knowledge why this happens and why the reconnect above helps is probably lost.
          // Set to stumble only in development. Once encountered, please document what kind
          // of issue happens there, why reconnect helps and if there are possible future
          // improvements.
          stumble(
            `Unauthorized access to socket server ${JSON.stringify(msg)}`,
          );
        }
      }
    });

    ipfSocket.socket.on('snapshot:start', snapshotStart);

    ipfSocket.socket.on('snapshot:done', (message: unknown) => {
      reduxDispatch(fetchSnapshotData());
      reduxDispatch(snapshotStatusReceived(message));
      reduxDispatch(enableStartBtn());
    });

    ipfSocket.socket.on('system:maxBandwidth', (message: unknown) =>
      reduxDispatch(networkBandwidthSet(message)),
    );
    ipfSocket.socket.on('logout', () => {
      navigate('/logout');
    });
    ipfSocket.socket.on('systemStatus', (message: unknown) => {
      queryClient.setQueryData(supportStatusQueryKey, message);
    });

    ipfSocket.socket.on(
      'updateAvailable',
      ({ version }: { version: string }) => {
        globalStateCtxProps.dispatch(saveIpfNewVersion, { version });
      },
    );

    ipfSocket.socket.on('job:queued', (message: unknown) =>
      reduxDispatch(jobQueued(message)),
    );
    ipfSocket.socket.on('job:created', (message: unknown) =>
      reduxDispatch(jobAdded(message)),
    );
    ipfSocket.socket.on('job:updated', (message: unknown) =>
      reduxDispatch(jobUpdated(message)),
    );
    ipfSocket.socket.on('job:cancelled', (message: unknown) =>
      reduxDispatch(jobCancelled(message)),
    );
    ipfSocket.socket.on('job:started', (message: unknown) =>
      reduxDispatch(jobStarted(message)),
    );

    let initialVersion: string | null = null;
    ipfSocket.socket.on('version', (version: string) => {
      localStorage.setItem('version', version);

      if (initialVersion === null) {
        // Store the initially received version for later comparison
        initialVersion = version;
      }

      if (initialVersion !== version) {
        // Reset initialVersion to prevent closing the refresh dialog by repeated toggling:
        // - new version arrives, toggleConfirmDialog opens dialog
        // - new version arrives again, toggleConfirmDialog would close dialog => not desired
        initialVersion = version;
        reduxDispatch(toggleConfirmDialog(REFRESH_DIALOGUE_NAME));
      }
    });
  }, []);
};
